import { CSSProperties, ReactNode, Ref, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton, MenuItem, Select, Stack, TextField } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  useErrorClearing,
  useGoalAssessmentValues,
  useGoalEditPermissions,
  useUniqueNameValidation,
} from 'hooks/GoalsAndSubgoalsForm';
import { TooltipInfoIcon, TooltipWithIcon } from 'components/Select';
import { isCuSatGoal } from 'helpers';
import { AssessmentScaleGroupAlias } from 'store/interfaces/ScorecardExternalSubgoalRO';
import { formTexts } from 'i18n';
import { GoalField } from 'components/GoalsAndSubgoalsForm/interfaces';
import { descriptionFieldSx } from 'pages/Scorecard/EditScorecard/GoalEditor.styled';
import {
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  RemoveColumn,
  SubgoalRoot,
} from 'pages/Scorecard/EditScorecard/SubgoalEditor.styled';

interface CommonGoalSubgoalEditorProps {
  goalIndex: number;
  subgoalIndex: number;
  onRemove: () => void;
  canDelete?: boolean;
  containerRef?: Ref<HTMLDivElement>;
  containerStyle?: CSSProperties;
  dragHandle?: ReactNode;
}

export default function CommonGoalSubgoalEditor({
  goalIndex,
  subgoalIndex,
  onRemove,
  canDelete = true,
  containerRef,
  containerStyle,
  dragHandle,
}: CommonGoalSubgoalEditorProps) {
  const { control, watch, setValue } = useFormContext<GoalField>();
  const { formatMessage } = useIntl();
  const path = `goals.${goalIndex}.subgoals.${subgoalIndex}` as const;
  const { assessmentScaleGroups: scaleGroups } = useGoalAssessmentValues(path);
  const goal = watch(`goals.${goalIndex}`);
  const subgoalId = goal?.subgoals?.[subgoalIndex]?.id;
  const isCuSat = isCuSatGoal(goal);
  const nameValidationRules = useUniqueNameValidation(
    `goals.${goalIndex}.subgoals`,
    subgoalIndex,
  );
  useErrorClearing(subgoalIndex, goalIndex);
  const { canEdit, canEditSubgoalWeights } = useGoalEditPermissions(
    goalIndex,
    false,
    false,
  );
  const selectedScaleId = useWatch<GoalField>({
    control,
    name: `${path}.assessmentScaleGroupId`,
  });
  const selectedScale = scaleGroups.find(
    (g) => String(g.id) === String(selectedScaleId),
  );
  const isTextAssessmentScale =
    selectedScale?.alias === AssessmentScaleGroupAlias.TEXT;

  // OLD CG behaviour: a TEXT-scale subgoal has no weight — force it to 0.
  useEffect(() => {
    if (isTextAssessmentScale) {
      setValue(`${path}.weight`, 0, { shouldValidate: true });
    }
  }, [path, isTextAssessmentScale, setValue]);

  const namePlaceholder = formatMessage({
    id: 'pages.scorecards.edit.subgoalNamePlaceholder',
    defaultMessage: 'Subgoal name',
  });
  const weightPlaceholder = formatMessage({
    id: 'pages.scorecards.edit.weightPlaceholder',
    defaultMessage: '0%',
  });
  const scalePlaceholder = formatMessage({
    id: 'pages.scorecards.edit.scalePlaceholder',
    defaultMessage: 'Standard Scale',
  });
  const descriptionPlaceholder = formatMessage({
    id: 'pages.scorecards.edit.subgoalDescriptionPlaceholder',
    defaultMessage: 'Add a description for this subgoal',
  });

  return (
    <SubgoalRoot
      ref={containerRef}
      style={containerStyle}
      id={typeof subgoalId === 'number' ? `subgoal-${subgoalId}` : undefined}
    >
      {dragHandle}
      <FieldsGrid>
        <FieldGroup className="name">
          <FieldLabel htmlFor={`cg-subgoal-${goalIndex}-${subgoalIndex}-name`}>
            <FormattedMessage
              id="pages.scorecards.edit.subgoalName"
              defaultMessage="Subgoal Name"
            />
            <span className="required">*</span>
          </FieldLabel>
          <Controller
            name={`${path}.name`}
            control={control}
            rules={{
              required: formatMessage(formTexts.errorRequired),
              maxLength: {
                value: 256,
                message: formatMessage(formTexts.errorMaxLength, {
                  fieldName: '',
                  maxLength: 256,
                }),
              },
              validate: nameValidationRules,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id={`cg-subgoal-${goalIndex}-${subgoalIndex}-name`}
                fullWidth
                size="small"
                placeholder={namePlaceholder}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputProps={{ maxLength: 256 }}
                disabled={isCuSat || !canEdit}
              />
            )}
          />
        </FieldGroup>
        {!isCuSat && (
          <>
            <FieldGroup className="weight">
              <FieldLabel
                htmlFor={`cg-subgoal-${goalIndex}-${subgoalIndex}-weight`}
              >
                <FormattedMessage
                  id="pages.scorecards.edit.weightPct"
                  defaultMessage="Weight (%)"
                />
                {!isTextAssessmentScale && <span className="required">*</span>}
              </FieldLabel>
              <Controller
                name={`${path}.weight`}
                control={control}
                rules={
                  isTextAssessmentScale
                    ? {}
                    : { required: formatMessage(formTexts.errorRequired) }
                }
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue(`${path}.weightTouched`, true);
                    }}
                    id={`cg-subgoal-${goalIndex}-${subgoalIndex}-weight`}
                    fullWidth
                    size="small"
                    type="number"
                    placeholder={weightPlaceholder}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    disabled={!canEditSubgoalWeights || isTextAssessmentScale}
                    onFocus={(e) => e.target.select()}
                  />
                )}
              />
            </FieldGroup>
            <FieldGroup className="scale">
              <FieldLabel>
                <FormattedMessage
                  id="pages.scorecards.edit.assessmentScale"
                  defaultMessage="Assessment Scale"
                />
                <span className="required">*</span>
              </FieldLabel>
              <Controller
                name={`${path}.assessmentScaleGroupId`}
                control={control}
                rules={{ required: formatMessage(formTexts.errorRequired) }}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    fullWidth
                    size="small"
                    displayEmpty
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    disabled={!canEdit}
                    renderValue={(value) => {
                      if (!value) return scalePlaceholder;
                      const group = scaleGroups.find(
                        (g) => String(g.id) === String(value),
                      );
                      return group?.name ?? scalePlaceholder;
                    }}
                  >
                    {scaleGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Stack direction="row" alignItems="center" width="100%">
                          {group.name}
                          {group.tooltipWithIcon && (
                            <TooltipWithIcon
                              title={group.tooltipWithIcon}
                              disableInteractive
                            >
                              <TooltipInfoIcon />
                            </TooltipWithIcon>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FieldGroup>
            {canDelete && (
              <RemoveColumn className="remove">
                <IconButton
                  onClick={onRemove}
                  size="small"
                  sx={{ color: '#6E7071' }}
                  aria-label="remove subgoal"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </RemoveColumn>
            )}
          </>
        )}
      </FieldsGrid>
      <FieldGroup>
        <FieldLabel
          htmlFor={`cg-subgoal-${goalIndex}-${subgoalIndex}-description`}
        >
          <FormattedMessage
            id="pages.scorecards.edit.description"
            defaultMessage="Description"
          />
        </FieldLabel>
        <Controller
          name={`${path}.description`}
          control={control}
          rules={{
            maxLength: {
              value: 3000,
              message: formatMessage(formTexts.errorMaxLength, {
                fieldName: '',
                maxLength: 3000,
              }),
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              id={`cg-subgoal-${goalIndex}-${subgoalIndex}-description`}
              fullWidth
              size="small"
              multiline
              placeholder={descriptionPlaceholder}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{ maxLength: 3000 }}
              disabled={isCuSat || !canEdit}
              sx={descriptionFieldSx}
            />
          )}
        />
      </FieldGroup>
    </SubgoalRoot>
  );
}
